import React from 'react';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import { AccountCircle, ExpandMoreSharp } from '@mui/icons-material';
import { useL10n, useTabsSyncedCallbackTrigger } from '@uplift/hooks';
import { useCurrentuser } from '@uplift/contexts';
import { ProfileMenuItem } from '@uplift/types';

import { AutoLogoutTrigger } from '../../../molecules/Modals/auto-logout-notification';
import { useStyles } from './profile-menu.styles';
import { Loader } from '../../../Loader';
import { useUserDisplayNameQuery } from '@uplift/queries';


export type ProfileMenuProps = {
  showAvatar?: boolean;
  items?: ProfileMenuItem[];
};


export const ProfileMenu: React.FC<ProfileMenuProps> = ({ items, showAvatar = true }) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const [ anchorEl, setAnchorEl ] = React.useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const menuId = 'profile-menu';
  const me = useCurrentuser();
  const logoutTrigger = useTabsSyncedCallbackTrigger(AutoLogoutTrigger.logoutManual);
  const { data: displayName, isLoading: isProfileLoading } = useUserDisplayNameQuery(me.id, { enabled: !!me?.id });
  const userName = displayName?.full ?? `${me.firstName} ${me.lastName}`;

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const logout = React.useCallback(() => {
    logoutTrigger();
  }, [ logoutTrigger ]);

  const renderMenu = React.useMemo(() => (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      className={classes.menuList}
      disablePortal
    >
      {items?.map((item, idx) => (
        <MenuItem onClick={item.onClick} key={idx}>
          {item.label}
        </MenuItem>
      ))}
      <MenuItem onClick={logout}>{l10n.auth.logout}</MenuItem>
    </Menu>
  ), [ anchorEl, classes.menuList, isMenuOpen, items, l10n.auth.logout, logout ]);

  if (isProfileLoading) {
    return <Loader color="primary" height="auto" width="auto" />;
  }

  return (
    <div>
      <Box className={classes.profileBlock} onClick={handleProfileMenuOpen}>
        {showAvatar && (
          <IconButton
            edge="end"
            aria-label="account of current user"
            aria-controls={menuId}
            aria-haspopup="true"
            color="primary"
          >
            <span className={classes.accountAvatarWrapper}>
              <AccountCircle fontSize="large" />
            </span>
          </IconButton>
        )}
        <Box className={classes.userName}>{userName}</Box>
        <ExpandMoreSharp />
      </Box>

      {renderMenu}
    </div>
  );
};
